<template>
  <div class="wiki-editor-container" :class="[outerClass, { 'read-only': readOnly }]">
    <slot name="topLeftContent"/>
    <slot name="readOnlyMessage"/>
    <div :class="editorWrapperClass">
      <div
        :class="['focus-border-wrapper', { 'has-focus': isFocused && !isEditMode }]"
      >
        <div
          :class="['rounded', 'min-h-24', 'font-default', 'relative', 'overflow-hidden', { 'edit-mode': isEditMode, 'cursor-not-allowed': readOnly }]"
          @keydown.enter.prevent="setIsEditMode(true)"
          @mouseenter="isHovering = true"
          @mouseleave="isHovering = false"
          @focus="isFocused = true"
          @blur="isFocused = false"
          :tabindex="readOnly ? -1 : 0"
          :aria-label="!readOnly && !isEditMode ? 'Click anywhere to begin editing' : undefined"
        >
          <editor-content :editor="editor" class="content-editor fs-exclude"/>
          <div
            v-if="(isHovering || isFocused) && !isEditMode && !readOnly"
            class="edit-tooltip"
          >
            Click anywhere to begin editing
          </div>
        </div>
      </div>
      <slot name="innerContent"/>
    </div>
    <div class="mt-4"><slot name="innerBottomContent"/></div>
    <div class="edit-button-container flex justify-between mt-4">
      <div class="flex items-center gap-2">
        <WikiTextEditorControls
          v-if="!hideControls"
          :isEditMode="isEditMode"
          :textToCopy="editorPlainText"
          :read-only="readOnly"
          @toggle="toggleEditMode"
        />
        <slot name="bottomLeftContent"/>
      </div>
      <slot name="bottomRightContent"/>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, watch, nextTick, computed, PropType, ref } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import { wikiToPlainText } from '@/util/tokenize_wiki_text'
import StarterKit from '@tiptap/starter-kit'
import Placeholder from '@tiptap/extension-placeholder'
import { Extension } from '@tiptap/core'
import WikiTextEditorControls from '@/components/editor/WikiTextEditorControls.vue'
import ResourceNode from './ResourceNode'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin.ts'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'

export default defineComponent({
  name: 'WikiTextEditor',
  components: {
    EditorContent,
    WikiTextEditorControls
  },

  props: {
    wikiText: {
      type: String,
      default: ''
    },
    primaryButtonTheme: {
      type: String,
      default: 'bluePrimary'
    },
    outerClass: {
      type: String,
      default: ''
    },
    editorWrapperClass: {
      type: String,
      default: ''
    },
    placeHolder: {
      type: String,
      default: ''
    },
    toEmbeddedText: {
      type: Function as PropType<(input: string) => string>,
      default: (input: string) => input,
    },
    toWikiText: {
      type: Function as PropType<(output: string) => string>,
      default: (output: string) => output,
    },
    resetValue: {
      type: String,
      default: ''
    },
    editedTextType: {
      type: String,
      default: ''
    },
    readOnly: {
      type: Boolean,
      default: false
    },
    hideControls: {
      type: Boolean,
      default: false
    }
  },
  emits: {
    'update:wikiText': (text: string) => typeof text === 'string',
    'editModeUpdated': (value: boolean) => typeof value === 'boolean'
  },
  setup(props, { emit }) {
    const getEditorText = () => props.toWikiText(editor.value?.getHTML() || props.wikiText)

    const isEditMode = ref(false)
    const isHovering = ref(false)
    const isFocused = ref(false)
    function setIsEditMode(newIsEditMode: boolean) {
      if (props.readOnly) {
        return
      }
      if (newIsEditMode === isEditMode.value) {
        return
      }
      isEditMode.value = newIsEditMode
      editor.value?.setEditable(newIsEditMode)
      if (newIsEditMode) {
        if (editor.value) {
          activateEditor()
        }
        analytics.sendEvent(EVENT_NAMES.TEXT_EDITOR.ACTIVATE_EDIT)
      } else {
        emit('update:wikiText', getEditorText())
        analytics.sendEvent(EVENT_NAMES.TEXT_EDITOR.DEACTIVATE_EDIT, {is_text_edited: isTextEdited.value})
      }
      emit('editModeUpdated', newIsEditMode)
    }
    function toggleEditMode() {
      setIsEditMode(!isEditMode.value)
    }

    // Computed property for plain text version (for copy functionality)
    const editorPlainText = computed(() => {
      return wikiToPlainText(editor.value?.getText() || '')
    })

    const KeyboardShortcuts = Extension.create({
      name: 'keyboardShortcuts',
      addKeyboardShortcuts() {
        return {
          'Escape': () => {
            setIsEditMode(false)
            return true
          },
        }
      },
    })

    // Watch for changes to wikiText prop and update editor content
    watch(() => props.wikiText, (newWikiText) => {
      if (!editor.value || !isEditMode.value) {
        const newContent = props.toEmbeddedText(newWikiText)
        if (editor.value && newWikiText !== getEditorText()) {
          editor.value.commands.setContent(newContent)
        }
      }
    })

    watch(() => props.placeHolder, (newPlaceHolder) => {
      if (editor.value) {
        editor.value.extensionManager.extensions.forEach((ext) => {
          if (ext.name === 'placeholder') {
            ext.options.placeholder = newPlaceHolder
          }
        })
      }
    })

    function activateEditor() {
      nextTick(() => {
        if (editor.value) {
          editor.value.commands.focus()
        }
      })
    }

    const isTextEdited = computed(() => {
      if (!props.resetValue && !editor.value?.getText()) {
        return false
      }
      return props.resetValue !== editor.value?.getHTML()
    })

    const analytics = getAnalyticsTracker().getChildContext({ edited_text_type: props.editedTextType })

    const editor = useEditor({
      autofocus: true,
      editable: isEditMode.value,
      editorProps: {
        handleClick: () => {
          if (!isEditMode.value) {
            setIsEditMode(true)
          }
          return true
        },
        attributes: {
          role: 'textbox',
          'aria-label': 'Content text editor',
        },
      },
      content: props.toEmbeddedText(props.wikiText),
      extensions: [
        StarterKit,
        Placeholder.configure({
          placeholder: props.placeHolder,
          showOnlyWhenEditable: false,
          emptyEditorClass: 'first:before:h-0 first:before:text-[#535353] first:before:italic first:before:float-left first:before:content-[attr(data-placeholder)] first:before:pointer-events-none',
        }),
        KeyboardShortcuts,
        ResourceNode
      ],
      // Update parent on blur
      onBlur: () => {
        setIsEditMode(false)
      },
      onUpdate: () => {
        if (!isEditMode.value) {
          const value = editor.value?.isEmpty ? '' : editor.value?.getHTML()
          emit('update:wikiText', props.toWikiText(value || ''))
        }
      }
    })

    return {
      editor,
      getEditorText,
      isEditMode,
      isHovering,
      isFocused,
      setIsEditMode,
      editorPlainText,
      activateEditor,
      toggleEditMode,
    }
  }
})
</script>
<style lang="postcss">
.tiptap {
  @apply border rounded-md;
  border-color: #BFD9EF;
  background-color: #EFF6FF;
  p {
    @apply p-1;
  }
  h1 {
    @apply text-xl font-bold mb-2;
  }
  ul, ol {
    @apply pl-5 mb-4;
  }
  ul {
    @apply list-disc;
  }
  ol {
    @apply list-decimal;
  }
  li {
    @apply mb-1;
  }
  strong {
    @apply font-bold;
  }
  em {
    @apply italic;
  }

  .mention-view {
    @apply inline-flex items-center gap-1 bg-blue-100 text-blue-500 px-2 py-0.5 rounded-sm;

    .icon {
      @apply text-sm;
    }

    .label {
      @apply text-sm;
    }
  }
}

.content-editor .ProseMirror {
  @apply min-h-24;
}

.wiki-editor-container .edit-mode {
  .tiptap {
    @apply rounded-md bg-yellow-100 border border-blue-400 outline-blue-400 shadow-wiki-editor;
  }
}

.focus-border-wrapper {
  @apply rounded-md;
  margin: -4px;
  padding: 4px;
  border: 2px solid transparent;
  border-bottom: 0 !important;
}

.focus-border-wrapper.has-focus {
  border-color: #004D72;
}

.wiki-editor-container:not(.read-only) > div > div > div:hover:not(.edit-mode) .tiptap {
  border-color: #007DBA;
}

.edit-tooltip {
  @apply absolute bg-black text-white pointer-events-none;
  right: 0;
  bottom: 0;
  padding: 4px 16px;
  font-size: 12px;
  z-index: 10;
  transform: translateY(2rem);
  animation: slideUp 0.3s ease-out forwards;
  border-radius: 0 0 6px 0;
}

@keyframes slideUp {
  to {
    transform: translateY(0);
  }
}
.white-mode {
  .tiptap {
    background-color: transparent;
    border-color: transparent;
  }
}
</style>
